<template>
  <!-- eslint-disable vue/no-v-html-->
  <wizard
    v-slot="{ currentStep }"
    first-step="personalDetails"
    class="wizard-location-service-tour"
  >
    <wizard-step-intro>
      <h2>{{ i18n.wizardSteps.locationServiceTour.title }}</h2>
      <slot name="location-service-tour-paragraph" />
    </wizard-step-intro>

    <wizard-step-indicator
      :steps="steps"
      :current-step="currentStep"
    />

    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      next-step="preferences"
      :gtm="triggerGtm"
    >
      <wizard-step-intro>
        <h3>{{ i18n.wizardSteps.personalDetails.title }}</h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12">
          <FormulateInput
            name="bonusCode"
            type="text"
            label="bonus code"
            validation="nohtml"
            class="custom-form-element"
          />
          <FormulateInput
            name="website"
            type="text"
            label="website"
            validation="nohtml"
            class="custom-form-element"
          />
          <FormulateInput
            name="salutation"
            :options="[{
              value: i18n.form.labels.salutationOptions.male,
              label: i18n.form.labels.salutationOptions.male,
            },{
              value: i18n.form.labels.salutationOptions.female,
              label: i18n.form.labels.salutationOptions.female,
            }]"
            type="radio"
            :label="i18n.form.labels.salutation"
            validation="required"
            :validation-messages="i18n.form.validations.salutation"
          />
        </div>

        <div class="col-span-12 md:col-span-4">
          <FormulateInput
            :label="i18n.form.labels.firstName"
            name="firstname"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.firstName"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormulateInput
            :label="i18n.form.labels.prefix"
            name="prefix"
            validation="bail|nohtml"
            :validation-messages="i18n.form.validations.prefix"
          />
        </div>
        <div class="col-span-12 md:col-span-5">
          <FormulateInput
            :label="i18n.form.labels.lastName"
            name="lastname"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.lastName"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="email"
            type="email"
            :label="i18n.form.labels.email"
            validation="bail|required:trim|email"
            :validation-messages="i18n.form.validations.email"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="phoneNumber"
            type="text"
            :label="i18n.form.labels.phoneNumber"
            validation="bail|required:trim|phonenumber|nohtml"
            :validation-messages="i18n.form.validations.phoneNumber"
          />
        </div>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="preferences"
      :model="formData"
      :next-step="sendRequest"
      previous-step="personalDetails"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
      :gtm="triggerGtm"
    >
      <wizard-step-intro>
        <h3>{{ i18n.wizardSteps.preferences.title }}</h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12">
          <FormulateInput
            name="days[]"
            :label="i18n.form.labels.preferredday"
            :options="{
              monday: i18n.form.labels.daysoptions.monday,
              tuesday: i18n.form.labels.daysoptions.tuesday,
              wednesday: i18n.form.labels.daysoptions.wednesday,
              thursday: i18n.form.labels.daysoptions.thursday,
              friday: i18n.form.labels.daysoptions.friday,
            }"
            type="checkbox"
            validation="required"
            :validation-messages="i18n.form.validations.days"
          />

          <FormulateInput
            name="remark"
            :label="i18n.form.labels.remark"
            type="textarea"
            validation="nohtml"
            :validation-messages="i18n.form.validations.remark"
          />

          <FormulateInput
            name="agree"
            type="checkbox"
            validation="required"
            :validation-messages="i18n.form.validations.agree"
          >
            <template #label="{ id }">
              <label
                :for="id"
                v-html="i18n.form.labels.agree"
              />
            </template>
          </FormulateInput>
        </div>
      </fieldset>
    </wizard-step>

    <slot name="location-service-tour-footer" />
  </wizard>
</template>

<script>
import Wizard from '@/components/wizards/components/Wizard.vue';
import WizardStep from '@/components/wizards/components/WizardStep.vue';
import WizardStepIndicator from '@/components/wizards/components/WizardStepIndicator.vue';
import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
import { gtmTemplates, trackEvent } from '@/lib/gtm/trackEvent';
import axios from 'axios';
import getQueryParams from '@/lib/formulate/getFormQueryParam';

export default {
  name: 'WizardLocationServiceTour',
  components: {
    Wizard,
    WizardStep,
    WizardStepIntro,
    WizardStepIndicator,
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    prefillData: {
      type: Object,
      default: () => { },
    },
    location: {
      type: Object,
      default: null,
    },
    childcareType: {
      type: Object,
      required: true,
    },
    locale: {
      type: String,
      default: () => 'nl',
    },
    label: {
      type: String,
      default: 'Partou',
    },
    successPageUrl: {
      type: String,
      default: () => '/',
    },
    apiHostName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      formData: {
        personalDetails: {},
        preferences: {},
      },
      request: {
        isSending: false,
        errorSending: false,
      },
      steps: [{
        key: 'personalDetails',
        name: this.i18n.form.steps.personalDetails,
        completed: false,
        active: false,
      }, {
        key: 'preferences',
        name: this.i18n.form.steps.preference,
        completed: false,
        active: false,
      }],
    };
  },
  created () {
    this.formData.personalDetails = {
      ...this.formData.personalDetails,
      ...getQueryParams(this.prefillData)
    }

  },
  methods: {
    triggerGtm(eventAction, additionalData) {
      const variableGtmData = {
        ...gtmTemplates.tourData,
        childcare_type: this.childcareType.childcareTypeName,
        location: this.location.userZipcode,
        school: this.childcareType === 'After school care' ? this.location.locationName : this.location.locationAddress,
        event_action: !eventAction ? 'registration_preferences' : 'registration_personal_data',
      }
      trackEvent({...gtmTemplates.tourData, ...variableGtmData, ...additionalData}, this.$gtm.enabled())
      return false
    },

    sendRequest() {
      this.request.errorSending = false;
      this.request.isSending = true;

      const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]')?.value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        }
      };

      const dataToPost = {
        careType: this?.childcareType?.childcareTypeKey,
        locale: this.locale,
        locationName: this?.location?.locationName || '',
        locationEmailAddress: this?.location?.locationEmailAddress || '',
        locationAddress: this?.location?.locationAddress || '',
        locationAddress2: this?.location?.locationAddress2 || '',
        locationPostalCode: this?.location?.locationPostalCode || '',
        locationCity: this?.location?.locationCity || '',
        locationLmsId: this?.location?.locationLmsId || '',
        locationMdmId: this?.location?.locationMdmId || '',
        isPartouLocation: this?.location?.locationIsPartouLocation === 'true' ? true : false,
        website: this.formData.personalDetails.website || '',
        bonusCode: this.formData.personalDetails.bonusCode || '',
        title: this.formData.personalDetails.salutation,
        firstName: this.formData.personalDetails.firstname,
        preposition: this.formData.personalDetails?.prefix || '',
        surname: this.formData.personalDetails.lastname,
        emailAddress: this.formData.personalDetails.email,
        telephoneNumber: this.formData.personalDetails.phoneNumber,
        days: this.formData.preferences['days[]'],
        comments: this.formData.preferences?.remark || '',
        acceptTermsAndConditions: this.formData.preferences.agree,

      };

      axios
        .post(`${this.apiHostName}/umbraco/api/GuidedTourBookingForm/Submit`, dataToPost, config)
        .then(() => {
          // Registration Preferences
          this.triggerGtm(false, {count_days: this.formData.preferences['days[]'].length }) ;
          // Registration Complete
          this.triggerGtm(false, { count_days: this.formData.preferences['days[]'].length, event_action: 'registration_complete'});
          window.location.href = this.successPageUrl;
        })
        .catch(() => {
          this.request.isSending = false;
          this.request.errorSending = true;
        });
    },
  }
};
</script>
